import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Typography, Box, Button, Grid, Paper,
    Chip, Avatar, AvatarGroup, CircularProgress, Collapse,
} from '@mui/material';
import {
    Add as AddIcon,
    ExpandMore as ExpandIcon,
    IosShare as ShareIcon,
    CalendarMonth as CalendarIcon,
    AccessTime as TimeIcon,
    Place as PlaceIcon,
    Link as LinkIcon,
    ArrowForward as ArrowIcon,
    Star as StarIcon,
    Bolt as FlashIcon,
    CheckCircle as ConfirmedIcon,
    ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { events } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { shareOrCopy } from '../utils/share';

const AVATAR_COLORS = ['#7C3AED', '#2563EB', '#059669', '#DC2626', '#D97706', '#0891B2'];

const useInView = (threshold = 0.12) => {
    const ref    = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);
    return [ref, inView];
};

// ── Animated section wrapper ──────────────────────────────────────────────────

const ParallaxSection = ({ children, delay = 0 }) => {
    const [ref, inView] = useInView();
    return (
        <Box
            ref={ref}
            sx={{
                opacity:   inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(48px)',
                transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
                willChange: 'opacity, transform',
            }}
        >
            {children}
        </Box>
    );
};

// ── Staggered card ────────────────────────────────────────────────────────────

const StaggerCard = ({ children, index }) => {
    const [ref, inView] = useInView(0.05);
    const delay = 0.05 + index * 0.07;
    return (
        <Box
            ref={ref}
            sx={{
                opacity:   inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(32px)',
                transition: `opacity 0.55s ease ${delay}s, transform 0.55s ease ${delay}s`,
                willChange: 'opacity, transform',
                height: '100%',
            }}
        >
            {children}
        </Box>
    );
};

// ── Dashboard ─────────────────────────────────────────────────────────────────

const Dashboard = () => {
    const navigate  = useNavigate();
    const { user }  = useAuth();

    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [pendingEvents,  setPendingEvents]  = useState([]);
    const [quickMeets,         setQuickMeets]         = useState([]);
    const [finishedQuickMeets, setFinishedQuickMeets] = useState([]);
    const [copiedId,       setCopiedId]       = useState(null);
    const [loading,        setLoading]        = useState(true);
    const [actionsOpen,    setActionsOpen]    = useState(false);

    const hour      = new Date().getHours();
    const greeting  = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
    const firstName = user?.firstName || '';

    useEffect(() => {
        if (!user?.id) return;
        Promise.all([
            events.getUpcoming(user.id),
            events.getPendingVotes(user.id),
            api.get('/quick-meets'),
        ]).then(([upRes, pendRes, qmRes]) => {
            const now = new Date();

            // Filter out past events — check startTime, or fall back to the selected slot
            setUpcomingEvents((upRes.data || []).filter(e => {
                if (e.startTime) return new Date(e.startTime) > now;
                const selected = e.timeSlots?.find(s => s.selected);
                if (selected) return new Date(selected.proposedStartTime) > now;
                return true; // no time confirmed yet — keep it
            }));

            setPendingEvents(pendRes.data);

            // A quick meet is "finished" once voting has ended: both parties voted
            // (CONFIRMED / CANCELLED) or the date passed (every proposed slot is in the past).
            const isFinishedQm = (qm) => {
                if (qm.status === 'CONFIRMED' || qm.status === 'CANCELLED') return true;
                if (qm.slots?.length > 0) return qm.slots.every(s => new Date(s.proposedStartTime) <= now);
                return false;
            };
            const allQm = qmRes.data || [];
            setQuickMeets(allQm.filter(qm => !isFinishedQm(qm)));
            setFinishedQuickMeets(allQm.filter(isFinishedQm));
        }).catch(err => console.error('Error fetching events:', err))
          .finally(() => setLoading(false));
    }, [user]);

    const bothEmpty = !loading && pendingEvents.length === 0 && upcomingEvents.length === 0 && quickMeets.length === 0 && finishedQuickMeets.length === 0;

    const handleShareQm = async (qm) => {
        const url = `${window.location.origin}/meet/${qm.shareToken}`;
        const result = await shareOrCopy({ title: qm.title, text: `Find a time to meet: ${qm.title}`, url });
        if (result === 'copied') {
            setCopiedId(qm.id);
            setTimeout(() => setCopiedId(null), 2000);
        }
    };

    const handleUpgrade = async () => {
        try {
            const r = await api.post('/subscriptions/checkout');
            window.location.href = r.data.url;
        } catch (e) {
            console.error('Could not start checkout:', e);
            navigate('/availability');   // fallback so the user still lands somewhere useful
        }
    };

    // ── Quick Meet Card ─────────────────────────────────────────────────────────
    const QuickMeetCard = ({ qm, index, finished = false }) => {
        const isConfirmed = qm.status === 'CONFIRMED';
        const isCancelled = qm.status === 'CANCELLED';
        const isExpired   = finished && !isConfirmed && !isCancelled;
        const label = isConfirmed ? 'Confirmed' : isCancelled ? 'Cancelled' : isExpired ? 'Expired' : 'Pending';

        // Accent: confirmed = green, expired/cancelled = gray, pending = purple
        const accent = isConfirmed
            ? { border:'#86EFAC', bg:'#F0FDF4', strip:'linear-gradient(90deg,#22C55E,#16A34A)', chipBg:'#DCFCE7', chipBorder:'#86EFAC', chipText:'#16A34A' }
            : (isExpired || isCancelled)
            ? { border:'#E5E7EB', bg:'#FAFAFA', strip:'linear-gradient(90deg,#9CA3AF,#6B7280)', chipBg:'#F3F4F6', chipBorder:'#E5E7EB', chipText:'#6B7280' }
            : { border:'divider', bg:'background.paper', strip:'linear-gradient(90deg,#7C3AED,#4F46E5)', chipBg:'#EDE9FE', chipBorder:'#C4B5FD', chipText:'#7C3AED' };

        return (
            <Grid item xs={12} sm={6} md={4} lg={3}>
                <StaggerCard index={index}>
                    <Paper onClick={() => navigate(`/quick-meets/${qm.id}`)}
                        elevation={0} sx={{
                            borderRadius:3, border:'1px solid', cursor:'pointer',
                            borderColor: accent.border, bgcolor: accent.bg,
                            opacity: (isExpired || isCancelled) ? 0.85 : 1,
                            overflow:'hidden', height:'100%', display:'flex', flexDirection:'column',
                            transition:'all 0.22s ease',
                            '&:hover':{ transform:'translateY(-3px)', boxShadow:'0 10px 32px rgba(124,58,237,0.12)' },
                        }}>
                        <Box sx={{ height:3, background: accent.strip }} />
                        <Box sx={{ p:3, display:'flex', flexDirection:'column', flexGrow:1 }}>
                            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2 }}>
                                <Box sx={{ px:1.5, py:0.3, borderRadius:99, bgcolor: accent.chipBg, border:`1px solid ${accent.chipBorder}` }}>
                                    <Typography sx={{ fontSize:'0.68rem', fontWeight:700, color: accent.chipText, textTransform:'uppercase', letterSpacing:0.6 }}>
                                        {label}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.disabled">{qm.durationMinutes} min</Typography>
                            </Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb:1, lineHeight:1.3 }}>{qm.title}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mb:'auto' }}>
                                {qm.slots?.length} time option{qm.slots?.length !== 1 ? 's' : ''}
                                {isConfirmed && qm.confirmedSlot && (
                                    <> · {new Date(qm.confirmedSlot.proposedStartTime).toLocaleDateString([], { month:'short', day:'numeric' })}</>
                                )}
                            </Typography>
                            {!finished && !isConfirmed && (
                                <Button size="small" startIcon={copiedId===qm.id ? <ConfirmedIcon sx={{ fontSize:14 }} /> : <ShareIcon sx={{ fontSize:14 }} />}
                                    onClick={e => { e.stopPropagation(); handleShareQm(qm); }}
                                    sx={{ mt:2, fontWeight:700, borderRadius:99, border:'1px solid #C4B5FD', color:'#7C3AED', fontSize:'0.75rem', '&:hover':{ bgcolor:'#EDE9FE' } }}>
                                    {copiedId===qm.id ? 'Copied!' : 'Share'}
                                </Button>
                            )}
                        </Box>
                    </Paper>
                </StaggerCard>
            </Grid>
        );
    };

    // ── Event Card ────────────────────────────────────────────────────────────
    const EventCard = ({ event, isPending }) => {
        const participants = event.participants || [];
        const [hovered, setHovered] = useState(false);

        return (
            <Paper
                onClick={() => navigate(`/events/${event.id}`)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                elevation={hovered ? 6 : 0}
                sx={{
                    p: 0,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: hovered ? 'transparent' : 'divider',
                    boxShadow: hovered
                        ? '0 12px 40px rgba(108, 92, 231, 0.14)'
                        : '0 1px 4px rgba(0,0,0,0.04)',
                    cursor: 'pointer',
                    transition: 'all 0.22s ease',
                    transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Gradient accent strip */}
                <Box sx={{
                    height: 3,
                    background: isPending
                        ? 'linear-gradient(90deg, #F59E0B 0%, #EF4444 100%)'
                        : 'linear-gradient(90deg, #6C5CE7 0%, #A29BFE 100%)',
                }} />

                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        {event.startTime ? (
                            <Chip
                                label={format(new Date(event.startTime), 'MMM d')}
                                size="small"
                                sx={{
                                    bgcolor: isPending ? '#FFF8E7' : '#EDE9FE',
                                    color:   isPending ? '#B45309' : '#6C5CE7',
                                    fontWeight: 700, fontSize: 12,
                                    height: 24, border: 'none',
                                }}
                            />
                        ) : <Box />}
                        <Chip
                            label={isPending ? 'Vote needed' : 'Upcoming'}
                            size="small"
                            variant="outlined"
                            color={isPending ? 'warning' : 'success'}
                            sx={{ fontSize: 11, height: 22 }}
                        />
                    </Box>

                    <Typography
                        variant="h6" fontWeight={700}
                        sx={{
                            mb: 1.5, lineHeight: 1.3, fontSize: '1.05rem',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {event.title}
                    </Typography>

                    {event.startTime && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                            <TimeIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
                            <Typography variant="caption" color="text.secondary">
                                {format(new Date(event.startTime), 'EEEE · h:mm a')}
                            </Typography>
                        </Box>
                    )}

                    {event.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <PlaceIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
                            <Typography variant="caption" color="text.secondary"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {event.location}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 'auto', pt: 2,
                        borderTop: '1px solid', borderColor: 'divider',
                    }}>
                        {participants.length > 0 ? (
                            <AvatarGroup max={5} sx={{
                                '& .MuiAvatar-root': { width: 26, height: 26, fontSize: 10, border: '2px solid white' },
                            }}>
                                {participants.map((p, i) => (
                                    <Avatar key={p.id || i} sx={{ bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                                        {(p.firstName?.[0] || p.username?.[0] || '?').toUpperCase()}
                                    </Avatar>
                                ))}
                            </AvatarGroup>
                        ) : <Box />}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.25,
                            opacity: hovered ? 1 : 0,
                            transform: hovered ? 'translateX(0)' : 'translateX(-4px)',
                            transition: 'all 0.2s ease',
                        }}>
                            <Typography variant="caption" color="primary.main" fontWeight={600}>View</Typography>
                            <ArrowIcon sx={{ fontSize: 13, color: 'primary.main' }} />
                        </Box>
                    </Box>
                </Box>
            </Paper>
        );
    };

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', overflowX: 'hidden' }}>

            {/* ── Hero header — static, no parallax ────────────────────── */}
            <Box sx={{
                background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                borderBottom: '1px solid',
                borderColor: 'divider',
                pt: 5, pb: 4,
            }}>
                <Container maxWidth="xl">
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: 3,
                    }}>
                        <Box>
                            <Typography variant="body2" color="text.disabled"
                                fontWeight={500} sx={{ mb: 0.75, letterSpacing: 0.3 }}>
                                {format(new Date(), 'EEEE, MMMM d')}
                            </Typography>
                            <Typography variant="h3" fontWeight={800}
                                sx={{ letterSpacing: -1, lineHeight: 1.1, mb: 2 }}>
                                {greeting}{firstName ? `, ${firstName}` : ''}.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                                {pendingEvents.length > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                                        <Typography variant="body2" fontWeight={600} color="warning.dark">
                                            {pendingEvents.length} {pendingEvents.length === 1 ? 'vote' : 'votes'} needed
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box sx={{
                                        width: 8, height: 8, borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #6C5CE7, #A29BFE)',
                                    }} />
                                    <Typography variant="body2" fontWeight={500} color="text.secondary">
                                        {upcomingEvents.length} upcoming {upcomingEvents.length === 1 ? 'event' : 'events'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box>
                            <Button variant="contained" startIcon={<AddIcon />}
                                endIcon={<ExpandIcon sx={{ transition:'transform 0.2s', transform: actionsOpen ? 'rotate(180deg)' : 'none' }} />}
                                onClick={() => setActionsOpen(v => !v)} size="small"
                                sx={{ fontWeight:700, borderRadius:2, px:2, fontSize:'0.82rem',
                                      background:'linear-gradient(135deg,#7C3AED,#4F46E5)',
                                      '&:hover':{ background:'linear-gradient(135deg,#6D28D9,#4338CA)' } }}>
                                New
                            </Button>
                            <Collapse in={actionsOpen}>
                                <Box sx={{ display:'flex', flexDirection:{ xs:'column', sm:'row' }, gap:1, flexWrap:'wrap', mt:1.5 }}>
                                    <Button variant="outlined" startIcon={<FlashIcon />} fullWidth
                                        onClick={() => navigate('/create-quick-meet')} size="small"
                                        sx={{ fontWeight:600, borderRadius:2, px:2, fontSize:'0.82rem', borderColor:'#7C3AED', color:'#7C3AED', width:{ sm:'auto' }, '&:hover':{ borderColor:'#6D28D9', bgcolor:'#EDE9FE' } }}>
                                        Quick Meet
                                    </Button>
                                    <Button variant="outlined" startIcon={<CalendarIcon />} fullWidth
                                        onClick={() => navigate('/create-event')} size="small"
                                        sx={{ fontWeight:600, borderRadius:2, px:2, fontSize:'0.82rem', borderColor:'#7C3AED', color:'#7C3AED', width:{ sm:'auto' }, '&:hover':{ borderColor:'#6D28D9', bgcolor:'#EDE9FE' } }}>
                                        Create Event
                                    </Button>
                                    <Button variant="outlined" startIcon={<TimeIcon />} fullWidth
                                        onClick={() => navigate('/availability')} size="small"
                                        sx={{ fontWeight:600, borderRadius:2, px:2, fontSize:'0.82rem', borderColor:'#7C3AED', color:'#7C3AED', width:{ sm:'auto' }, '&:hover':{ borderColor:'#6D28D9', bgcolor:'#EDE9FE' } }}>
                                        Availability
                                    </Button>
                                </Box>
                            </Collapse>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* ── Content ──────────────────────────────────────────────────── */}
            <Container maxWidth="xl" sx={{ pt: 4, pb: 8 }}>

                {/* ── Free-tier upgrade nudge ────────────────────────────── */}
                {user?.tier !== 'PREMIUM' && (
                    <Box
                        onClick={handleUpgrade}
                        sx={{
                            mb: 3,
                            px: 3, py: 2,
                            borderRadius: 2.5,
                            background: 'linear-gradient(90deg, #6C5CE7 0%, #A29BFE 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            cursor: 'pointer',
                            boxShadow: '0 4px 20px rgba(108,92,231,0.25)',
                            transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 28px rgba(108,92,231,0.35)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <StarIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                            <Typography fontWeight={600} sx={{ color: 'white', fontSize: '0.9rem' }}>
                                Upgrade to Premium — unlimited availability slots &amp; more
                            </Typography>
                        </Box>
                        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', fontWeight: 500 }}>
                            $9/mo &nbsp;→
                        </Typography>
                    </Box>
                )}

                {/* ── Empty state ────────────────────────────────────────── */}
                {bothEmpty && (
                    <ParallaxSection>
                        <Box sx={{
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            textAlign: 'center', pt: 6, pb: 10,
                        }}>
                            <Box sx={{ position: 'relative', mb: 5, width: 120, height: 120 }}>
                                <Box sx={{
                                    position: 'absolute', inset: -20, borderRadius: '50%',
                                    background: 'radial-gradient(circle, rgba(108,92,231,0.15) 0%, transparent 70%)',
                                }} />
                                <Box sx={{
                                    position: 'relative', zIndex: 1,
                                    width: 120, height: 120, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 16px 48px rgba(108, 92, 231, 0.35)',
                                }}>
                                    <CalendarIcon sx={{ fontSize: 52, color: 'white' }} />
                                </Box>
                            </Box>

                            <Typography variant="h4" fontWeight={800}
                                sx={{ letterSpacing: -0.5, mb: 1.5 }}>
                                Nothing on the calendar yet
                            </Typography>
                            <Typography variant="body1" color="text.secondary"
                                sx={{ mb: 5, maxWidth: 420, lineHeight: 1.7 }}>
                                Create your first event and invite people to vote on the perfect time —
                                or share your booking link so others can find a time with you.
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                                <Button variant="contained" startIcon={<AddIcon />}
                                    onClick={() => navigate('/create-quick-meet')} size="large"
                                    sx={{ fontWeight: 700, borderRadius: 2, px: 4, py: 1.5, background:'linear-gradient(135deg,#7C3AED,#4F46E5)', boxShadow:'0 4px 14px rgba(124,58,237,0.3)' }}>
                                    Send a Quick Meet
                                </Button>
                                <Button variant="outlined" startIcon={<AddIcon />}
                                    onClick={() => navigate('/create-event')} size="large"
                                    sx={{ fontWeight: 700, borderRadius: 2, px: 4, py: 1.5 }}>
                                    Create an event
                                </Button>
                                <Button variant="outlined" startIcon={<LinkIcon />}
                                    onClick={() => navigate('/availability')} size="large"
                                    sx={{ fontWeight: 700, borderRadius: 2, px: 3, py: 1.5 }}>
                                    Share booking link
                                </Button>
                            </Box>
                        </Box>
                    </ParallaxSection>
                )}

                {/* ── Quick Meets ────────────────────────────────────────── */}
                {quickMeets.length > 0 && (
                    <ParallaxSection delay={0}>
                        <Box sx={{ mb:4 }}>
                            <Box sx={{ display:'flex', alignItems:'center', mb:2.5, gap:1.5 }}>
                                <Box sx={{ width:10, height:10, borderRadius:'50%', background:'linear-gradient(135deg,#7C3AED,#4F46E5)', flexShrink:0 }} />
                                <Typography variant="caption" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:1, color:'#5848C2' }}>
                                    Quick Meets
                                </Typography>
                                <Box sx={{ width:20, height:20, borderRadius:'50%', bgcolor:'#EDE9FE', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                    <Typography sx={{ fontSize:11, fontWeight:700, color:'#7C3AED' }}>{quickMeets.length}</Typography>
                                </Box>
                            </Box>

                            <Grid container spacing={2.5}>
                                {quickMeets.map((qm, i) => <QuickMeetCard key={qm.id} qm={qm} index={i} />)}
                            </Grid>
                        </Box>
                    </ParallaxSection>
                )}

                {/* ── Finished Quick Meets ───────────────────────────────── */}
                {finishedQuickMeets.length > 0 && (
                    <ParallaxSection delay={0}>
                        <Box sx={{ mb:4 }}>
                            <Box sx={{ display:'flex', alignItems:'center', mb:2.5, gap:1.5 }}>
                                <Box sx={{ width:10, height:10, borderRadius:'50%', bgcolor:'#9CA3AF', flexShrink:0 }} />
                                <Typography variant="caption" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:1, color:'#6B7280' }}>
                                    Finished Quick Meets
                                </Typography>
                                <Box sx={{ width:20, height:20, borderRadius:'50%', bgcolor:'#F3F4F6', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                    <Typography sx={{ fontSize:11, fontWeight:700, color:'#6B7280' }}>{finishedQuickMeets.length}</Typography>
                                </Box>
                            </Box>
                            <Grid container spacing={2.5}>
                                {finishedQuickMeets.map((qm, i) => <QuickMeetCard key={qm.id} qm={qm} index={i} finished />)}
                            </Grid>
                        </Box>
                    </ParallaxSection>
                )}

                {/* ── Pending votes ──────────────────────────────────────── */}
                {pendingEvents.length > 0 && (
                    <ParallaxSection delay={0}>
                        <Box sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Box sx={{
                                    width: 10, height: 10, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #F59E0B, #EF4444)',
                                    flexShrink: 0,
                                }} />
                                <Typography variant="caption" fontWeight={700}
                                    sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#B45309' }}>
                                    Needs your vote
                                </Typography>
                                <Box sx={{
                                    width: 20, height: 20, borderRadius: '50%',
                                    bgcolor: '#FEF3C7', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#B45309' }}>
                                        {pendingEvents.length}
                                    </Typography>
                                </Box>
                            </Box>

                            <Grid container spacing={2.5}>
                                {pendingEvents.map((event, i) => (
                                    <Grid item key={event.id} xs={12} sm={6} md={4} lg={3}>
                                        <StaggerCard index={i}>
                                            <EventCard event={event} isPending />
                                        </StaggerCard>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </ParallaxSection>
                )}

                {/* ── Upcoming events ────────────────────────────────────── */}
                {upcomingEvents.length > 0 && (
                    <ParallaxSection delay={pendingEvents.length > 0 ? 0.1 : 0}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Box sx={{
                                    width: 10, height: 10, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #6C5CE7, #A29BFE)',
                                    flexShrink: 0,
                                }} />
                                <Typography variant="caption" fontWeight={700}
                                    sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#5848C2' }}>
                                    Upcoming
                                </Typography>
                            </Box>

                            <Grid container spacing={2.5}>
                                {upcomingEvents.map((event, i) => (
                                    <Grid item key={event.id} xs={12} sm={6} md={4} lg={3}>
                                        <StaggerCard index={i}>
                                            <EventCard event={event} isPending={false} />
                                        </StaggerCard>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </ParallaxSection>
                )}

                {!bothEmpty && pendingEvents.length === 0 && upcomingEvents.length === 0 && (
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                        <Typography color="text.disabled" variant="body2">
                            Nothing else to show right now.
                        </Typography>
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default Dashboard;
